import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Shield, MapPin, Lock, Mail } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-warm">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          ← Back
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">Privacy Policy</h1>
        </div>

        <Card className="p-8 shadow-warm border-border/50 space-y-6">
          <section>
            <p className="text-muted-foreground mb-4">Last updated: {new Date().toLocaleDateString()}</p>
            <p>
              At imata, we believe in spreading joy while protecting your privacy. This policy explains how we collect,
              use, and safeguard your information.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Location Data
            </h2>
            <p className="text-muted-foreground">
              We use your location to show nearby users who are open to receiving gifts. Your exact location is never
              shared—only approximate distance and general area.
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Location access is optional and can be disabled anytime</li>
              <li>We only store approximate location (within 0.5 miles)</li>
              <li>Location data is not sold or shared with third parties</li>
              <li>You control your visibility on the map</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Lock className="w-5 h-5 text-secondary" />
              Gifting Visibility
            </h2>
            <p className="text-muted-foreground">When you send or receive gifts:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Personal messages are only visible to sender and recipient</li>
              <li>Gift amounts are private by default</li>
              <li>You can choose to make your gifting activity public</li>
              <li>All financial information is encrypted and secure</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Information We Collect</h2>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Email address (for authentication)</li>
              <li>Approximate location (when enabled)</li>
              <li>Gift transaction history</li>
              <li>App usage data for improving user experience</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Data Security</h2>
            <p className="text-muted-foreground">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>End-to-end encryption for sensitive data</li>
              <li>Secure payment processing through trusted providers</li>
              <li>Regular security audits and updates</li>
              <li>No storage of payment card information</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Your Rights</h2>
            <p className="text-muted-foreground">You have the right to:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Access your personal data</li>
              <li>Request data deletion</li>
              <li>Opt-out of location tracking</li>
              <li>Control your visibility settings</li>
              <li>Export your gifting history</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">Third-Party Services</h2>
            <p className="text-muted-foreground">We use trusted third-party services for:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Payment processing (PCI-compliant providers)</li>
              <li>Authentication services</li>
              <li>Analytics (anonymized data only)</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Mail className="w-5 h-5 text-accent" />
              Contact Us
            </h2>
            <p className="text-muted-foreground">Questions about your privacy? We're here to help:</p>
            <p className="text-muted-foreground">
              Email:{" "}
              <a href="mailto:privacy@peespees.com" className="text-primary hover:underline">
                privacy@peespees.com
              </a>
            </p>
          </section>

          <section className="pt-4 border-t border-border space-y-3">
            <p className="text-sm text-muted-foreground">
              By using imata, you agree to this Privacy Policy. We may update this policy occasionally and will notify
              you of significant changes.
            </p>
            <p className="text-sm text-muted-foreground">
              Related policies:{" "}
              <Link to="/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>
              {" | "}
              <Link to="/safety" className="text-primary hover:underline">
                Safety Policy
              </Link>
            </p>
          </section>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;
